"use client";

import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateKycFieldsRequest } from "@/store/slices/kyc.slice";
import { 
  KYCProblem, 
  getUpdatableFieldsFromProblems, 
  getProblemDescription 
} from "@/types/models/kyc";
import { Button } from "@/components/ui/button";
import { InputPrimary } from "@/components/common/InputPrimary";
import FormFieldWrapper from "@/components/coupon/formFieldWrapper";
import { Camera, Upload, AlertCircle, CheckCircle } from "lucide-react";
import { fileValidation } from "@/services/kycService";

interface KYCUpdateManagerProps {
  storeId: string;
  problems: KYCProblem[];
  onUpdateComplete?: () => void;
}

interface UpdateFields {
  business_name?: string;
  business_email?: string;
  business_phone_number?: string;
  business_logo?: File | null;
  address_line_one?: string;
  address_line_two?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  gst_number?: string;
  aadhaar_document_front?: File | null;
  aadhaar_document_back?: File | null;
  pan_document?: File | null;
  address_proof_document?: File | null;
  video_kyc_file?: File | null;
}

export const KYCUpdateManager: React.FC<KYCUpdateManagerProps> = ({
  storeId,
  problems,
  onUpdateComplete,
}) => {
  const dispatch = useAppDispatch();
  const { isLoading, error, kycData } = useAppSelector((state) => state.kyc);
  
  const [updateFields, setUpdateFields] = useState<UpdateFields>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Get updatable fields based on problems
  const updatableFields = getUpdatableFieldsFromProblems(problems);

  // Handle text field updates
  const handleTextFieldChange = (field: string, value: string) => {
    setUpdateFields(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Handle file uploads
  const handleFileUpload = (field: string, file: File | null) => {
    if (file) {
      // Validate file based on type
      let validation;
      if (field === "video_kyc_file") {
        validation = fileValidation.validateVideoFile(file);
      } else {
        validation = fileValidation.validateImageFile(file);
      }

      if (!validation.isValid) {
        setValidationErrors(prev => ({ 
          ...prev, 
          [field]: validation.error || "Invalid file" 
        }));
        return;
      }
    }

    setUpdateFields(prev => ({ ...prev, [field]: file }));
    // Clear validation error
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Validate required fields
  const validateFields = (): boolean => {
    const errors: Record<string, string> = {};

    updatableFields.forEach(field => {
      const value = updateFields[field as keyof UpdateFields];
      if (!value && field !== "address_line_two") {
        errors[field] = "This field is required";
      }

      // Specific validations
      if (field === "business_email" && value && typeof value === "string") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[field] = "Please enter a valid email address";
        }
      }

      if (field === "business_phone_number" && value && typeof value === "string") {
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(value)) {
          errors[field] = "Please enter a valid 10-digit phone number";
        }
      }

      if (field === "gst_number" && value && typeof value === "string") {
        if (value.length < 15) {
          errors[field] = "GST number must be at least 15 characters";
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateFields()) {
      return;
    }

    setSubmitError(null);

    try {
      // Filter out empty fields
      const fieldsToUpdate = Object.entries(updateFields)
        .filter(([, value]) => value !== undefined && value !== null && value !== "")
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      if (Object.keys(fieldsToUpdate).length === 0) {
        setSubmitError("Please update at least one field");
        return;
      }

      dispatch(updateKycFieldsRequest({
        storeId,
        fields: fieldsToUpdate,
      }));

      // Wait for completion
      // Note: In a real implementation, you might want to use a promise-based approach
      setTimeout(() => {
        if (onUpdateComplete) {
          onUpdateComplete();
        }
      }, 2000);

    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Failed to update KYC fields"
      );
    }
  };

  // Render field input based on field type
  const renderFieldInput = (field: string) => {
    const currentValue = kycData?.[field as keyof typeof kycData] as string;
    const error = validationErrors[field];

    // File upload fields
    if (field.includes("document") || field === "business_logo" || field === "video_kyc_file") {
      const isVideo = field === "video_kyc_file";
      const accept = isVideo ? "video/mp4,video/webm,video/quicktime" : "image/jpeg,image/png";
      const currentFile = updateFields[field as keyof UpdateFields] as File | null;

      return (
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 border-2 border-dashed rounded-lg flex items-center justify-center ${
              error ? "border-red-500 bg-red-50" : "border-gray-300"
            }`}>
              {currentFile ? (
                <CheckCircle className="w-6 h-6 text-green-500" />
              ) : (
                <Upload className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById(field)?.click()}
              >
                <Camera className="w-4 h-4 mr-2" />
                {currentFile ? "Change File" : "Upload File"}
              </Button>
              <p className="text-xs text-gray-500 mt-1">
                {isVideo ? "MP4/WebM, max 50MB" : "JPEG/PNG, max 2MB"}
              </p>
              <input
                id={field}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => handleFileUpload(field, e.target.files?.[0] || null)}
              />
            </div>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          {currentFile && <p className="text-green-600 text-sm">✓ File selected: {currentFile.name}</p>}
        </div>
      );
    }

    // Text fields
    return (
      <div className="space-y-2">
        <InputPrimary
          value={updateFields[field as keyof UpdateFields] as string || currentValue || ""}
          onChange={(e) => handleTextFieldChange(field, e.target.value)}
          placeholder={`Enter ${field.replace(/_/g, " ")}`}
          className={`${error ? "border-red-500 bg-red-50" : ""}`}
        />
        {error && <p className="text-red-500 text-sm">{error}</p>}
      </div>
    );
  };

  // Get field label
  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      business_name: "Business Name",
      business_email: "Business Email",
      business_phone_number: "Business Phone Number",
      business_logo: "Business Logo",
      address_line_one: "Address Line 1",
      address_line_two: "Address Line 2",
      city: "City",
      state: "State",
      postal_code: "Postal Code",
      gst_number: "GST Number",
      aadhaar_document_front: "Aadhaar Card (Front)",
      aadhaar_document_back: "Aadhaar Card (Back)",
      pan_document: "PAN Card",
      address_proof_document: "Address Proof Document",
      video_kyc_file: "Video KYC",
    };
    return labels[field] || field.replace(/_/g, " ");
  };

  if (updatableFields.length === 0) {
    return (
      <div className="text-center p-6 bg-gray-50 rounded-lg">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
        <p className="text-gray-600">No updates required</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-orange-800">
              Update Required Fields
            </h3>
            <p className="text-sm text-orange-700 mt-1">
              Please update the following fields to resolve the KYC issues:
            </p>
            <ul className="mt-2 space-y-1">
              {problems.map((problem, index) => (
                <li key={index} className="text-sm text-orange-700">
                  • {getProblemDescription(problem)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-6">
        {updatableFields.map((field) => (
          <FormFieldWrapper key={field} title={getFieldLabel(field)}>
            {renderFieldInput(field)}
          </FormFieldWrapper>
        ))}

        {/* Error Message */}
        {(error || submitError) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error || submitError}</p>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={isLoading || updatableFields.every(field => !updateFields[field as keyof UpdateFields])}
            className="px-8"
          >
            {isLoading ? "Updating..." : "Update KYC Fields"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default KYCUpdateManager;